import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, TrendingUp, Clock, AlertTriangle, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const TRCInventory = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load summary
      const summaryRes = await axios.get(
        `${API_URL}/api/trc/inventory/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (summaryRes.data.success) {
        setSummary(summaryRes.data);
      }

      // Load devices with filter
      const devicesRes = await axios.get(
        `${API_URL}/api/trc/devices${filter !== 'all' ? `?status=${filter}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (devicesRes.data.success) {
        setDevices(devicesRes.data.devices);
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">TRC Inventory Dashboard</h1>
          </div>
          <button
            onClick={loadData}
            className="px-4 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: ZOBOX_BLUE }}
          >
            Refresh
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-8 h-8 text-blue-600" />
              <span className="text-3xl font-bold text-gray-900">{summary?.total_devices || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Total Devices</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <span className="text-3xl font-bold text-gray-900">{summary?.sos_count || 0}</span>
            </div>
            <p className="text-sm text-gray-600">SOS Priority</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-orange-600" />
              <span className="text-3xl font-bold text-gray-900">
                {summary?.aging?.['30+_days'] || 0}
              </span>
            </div>
            <p className="text-sm text-gray-600">Aging (30+ days)</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <span className="text-3xl font-bold text-gray-900">
                {summary?.status_wise?.rfr || 0}
              </span>
            </div>
            <p className="text-sm text-gray-600">Ready for Repair</p>
          </div>
        </div>

        {/* Status Wise Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Status Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Status Distribution
            </h2>
            <div className="space-y-3">
              {Object.entries(summary?.status_wise || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {status.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Category Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Category Wise
            </h2>
            <div className="space-y-3">
              {Object.entries(summary?.category_wise || {}).map(([category, count]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 capitalize">{category}</span>
                  <span className="text-sm font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Aging Analysis */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Aging Analysis
          </h2>
          <div className="grid grid-cols-5 gap-4">
            {Object.entries(summary?.aging || {}).map(([bucket, count]) => (
              <div key={bucket} className="text-center">
                <div className="text-2xl font-bold text-gray-900">{count}</div>
                <div className="text-xs text-gray-600 mt-1">{bucket.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Devices List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Device List</h2>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border-2 rounded-lg"
              >
                <option value="all">All Devices</option>
                <option value="received">Received</option>
                <option value="binned">Binned</option>
                <option value="iqc">IQC</option>
                <option value="ntf">NTF</option>
                <option value="tf_pna">TF - PNA</option>
                <option value="rfr">RFR</option>
                <option value="assigned_to_engineer">Assigned to Engineer</option>
                <option value="repair_in_progress">Repair in Progress</option>
                <option value="repair_completed">Repair Completed</option>
                <option value="fqc_pass">FQC Pass</option>
                <option value="fqc_fail_rtr">FQC Fail</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">DID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engineer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days in TRC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {devices.map((device, index) => {
                  const daysInTRC = Math.floor(
                    (new Date() - new Date(device.created_at)) / (1000 * 60 * 60 * 24)
                  );

                  return (
                    <tr 
                      key={index} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/device/${device.trc_device_id || device.did}`)}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{device.did}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {device.brand} {device.model}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {device.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {device.assigned_engineer || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className={daysInTRC > 30 ? 'text-red-600 font-bold' : ''}>
                          {daysInTRC} days
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {device.is_sos && (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            SOS
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {devices.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No devices found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TRCInventory;
